import Clarity from "@microsoft/clarity";
import { Outlet, createFileRoute, redirect } from "@tanstack/react-router";
import { z } from "zod";
import { userApi } from "~/lib/api/user";
import { useEventStore } from "~/lib/stores/event";
import { useUserStore } from "~/lib/stores/user";

export const Route = createFileRoute("/_protected")({
	component: ProtectedLayout,
	beforeLoad: async ({
		context,
		location,
		matches,
		search,
	}): Promise<unknown> => {
		const data = await context.queryClient.ensureQueryData({
			queryKey: ["user", "partial"],
			queryFn: () => userApi.getPartialUser().catch(() => null),
		});

		if (!data || !data.data) {
			const _match = matches.find((m) => m.fullPath === "/event/$eventSlug") as
				| {
						params: { eventSlug: string };
				  }
				| undefined;
			if (_match) {
				return redirect({
					to: "/invite/$eventSlug",
					params: {
						eventSlug: _match.params.eventSlug,
					},
				});
			}
			return redirect({
				to: "/login",
				search: {
					...search,
					redirect: location.pathname,
				},
			});
		}

		useUserStore.getState().setUser({
			id: data.data.userId,
			firstName: data.data.firstName,
			lastName: data.data.lastName,
			username: data.data.username,
		});

		if (search.choice) {
			useEventStore.getState().clearChoice();
			useEventStore.getState().setChoice(search.choice);
		}

		Clarity.identify(data.data.userId);

		if (
			!data.data?.hasCompletedOnboarding &&
			!location.pathname.includes("/onboarding")
		) {
			return redirect({
				to: "/onboarding",
				search: {
					...search,
					redirect: location.pathname,
				},
			});
		}

		if (
			data.data?.hasCompletedOnboarding &&
			location.pathname.includes("/onboarding")
		) {
			return redirect({
				to: search.redirect?.length ? search.redirect : "/",
			});
		}
	},
	validateSearch: z.object({
		redirect: z.string().optional().catch(""),
		choice: z.enum(["accepted", "maybe", "declined"]).optional(),
	}),
});

function ProtectedLayout() {
	return <Outlet />;
}
